import React, { forwardRef, useState } from 'react';
import { cx } from 'linaria';
import { useMediaQuery } from 'fronton-react';
import { AkinatorCompletion, SlicedSuggestions } from 'types/suggestions';
import { suggestionsContainer, suggestionsRoot, suggestionsActive } from './styles';
import { Suggestions } from './suggestions';
import { SuggestionPreloader } from './suggestions-preloader';
import { Error } from './suggestions-error';
import { SuggestionContainerRefProvider } from '../hooks';
import { useVisualViewportApi } from 'common/hooks';
import { PinnedSuggestion } from 'types/model';

type SearchbarModalProps = {
  searchQuery: string;
  updateSearchQuery: (value: string) => void;
  suggestions: SlicedSuggestions | null;
  onMouseEnter: (idx: number) => void;
  onMouseLeave: () => void;
  fetchSuggestions: () => void;
  suggestionsLoading?: boolean;
  onSelectSuggestion?: () => void;
  logoUrl: string;
  searchHref: string;
  error?: boolean;
  expanded: boolean;
  focusGroup: string;
  suggestionSelector: string;
  index: number;
  isSrp: boolean;
  pinnedSuggestions: PinnedSuggestion[];
  completions: AkinatorCompletion[] | null;
  showAkinatorCompletions: boolean;
};

export const SearchbarModal = forwardRef<HTMLDivElement, SearchbarModalProps>(
  (
    {
      searchQuery,
      updateSearchQuery,
      suggestions,
      searchHref,
      onMouseEnter,
      fetchSuggestions,
      onMouseLeave,
      expanded,
      error,
      suggestionSelector,
      index,
      isSrp,
      onSelectSuggestion,
      suggestionsLoading,
      pinnedSuggestions,
      completions,
      showAkinatorCompletions,
    },
    ref,
  ) => {
    const showSuggestions = !error && suggestions;

    const isMobile = useMediaQuery({
      maxWidth: 767,
    });

    const [safeAreaHeight, setSafeAreaHeight] = useState('100vh');

    useVisualViewportApi({ handle: 'resize' }, (visualView) => {
      setSafeAreaHeight(`${visualView.height}px`);
    });

    const style = isMobile ? { height: safeAreaHeight } : {};

    return (
      <div className={cx(suggestionsRoot, expanded && suggestionsActive)} role="dialog">
        <div ref={ref} style={style} className={suggestionsContainer} data-qa="suggestions-container">
          <SuggestionContainerRefProvider containerRef={ref}>
            {error && <Error fetchSuggestions={fetchSuggestions} />}
            {suggestionsLoading && <SuggestionPreloader />}
            {showSuggestions && !suggestionsLoading && (
              <Suggestions
                searchQuery={searchQuery}
                updateSearchQuery={updateSearchQuery}
                onSelectSuggestion={onSelectSuggestion}
                isSrp={isSrp}
                suggestions={suggestions}
                suggestionSelector={suggestionSelector}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                searchHref={searchHref}
                index={index}
                pinnedSuggestions={pinnedSuggestions}
                completions={completions}
                showAkinatorCompletions={showAkinatorCompletions}
              />
            )}
          </SuggestionContainerRefProvider>
        </div>
      </div>
    );
  },
);
